import type {
  WorkflowRuntimeStatus
} from "@workerstation/shared";
import type { WorkflowStepRecord } from "../../api/workflows/model.ts";

// Adapted from workers-sdk's local-explorer-ui workflow helpers, converted
// from ISO-string timestamps to the epoch-millisecond fields this runtime
// records.

export function formatDuration(start?: number, end?: number): string {
  if (!start || !end) {
    return "—";
  }
  const ms = end - start;
  if (isNaN(ms) || ms < 0) {
    return "—";
  }
  if (ms < 1000) {
    return `${ms}ms`;
  }
  const secs = Math.floor(ms / 1000);
  const remainMs = ms % 1000;
  if (ms < 60_000) {
    return remainMs > 0 ? `${secs}s ${remainMs}ms` : `${secs}s`;
  }
  const mins = Math.floor(ms / 60_000);
  const remainSecs = Math.floor((ms % 60_000) / 1000);
  if (mins < 60) {
    return remainSecs > 0 ? `${mins}m ${remainSecs}s` : `${mins}m`;
  }
  const hours = Math.floor(mins / 60);
  const remainMins = mins % 60;
  return remainMins > 0 ? `${hours}h ${remainMins}m` : `${hours}h`;
}

export function timeAgo(timestamp: number | undefined): string {
  if (!timestamp) {
    return "";
  }
  const seconds = Math.floor((Date.now() - timestamp) / 1000);
  if (isNaN(seconds)) {
    return "";
  }
  if (seconds < 5) {
    return "just now";
  }
  if (seconds < 60) {
    return `${seconds}s ago`;
  }
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    return `${minutes}m ago`;
  }
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return `${hours}h ago`;
  }
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export function formatJson(value: unknown): string {
  if (value === null || value === undefined) {
    return "N/A";
  }
  if (typeof value === "string") {
    return value;
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

export type WorkflowUiAction = "pause" | "resume" | "restart" | "terminate";



export function getAvailableActions(
  status: WorkflowRuntimeStatus
): WorkflowUiAction[] {
  const actions: WorkflowUiAction[] = [];
  if (status === "running" || status === "waiting" || status === "queued") {
    actions.push("pause");
  }
  if (status === "paused" || status === "waitingForPause") {
    actions.push("resume");
  }
  if (!isTerminalStatus(status)) {
    actions.push("terminate");
  }
  actions.push("restart");
  return actions;
}

export function isTerminalStatus(status: WorkflowRuntimeStatus): boolean {
  return status === "complete" || status === "errored" || status === "terminated";
}

/**
 * Convert a recorded step into the `from` payload accepted by the restart
 * control. The runtime stores the 1-based occurrence counter directly on the
 * record; the platform API expects "do" for code steps and folds sleepUntil
 * into "sleep".
 */
export function restartFromStepParam(step: WorkflowStepRecord): {
  name: string;
  count: number;
  type: "do" | "sleep" | "waitForEvent";
} {
  return {
    name: step.name,
    count: step.count,
    type:
      step.kind === "waitForEvent"
        ? "waitForEvent"
        : step.kind === "sleep" || step.kind === "sleepUntil"
          ? "sleep"
          : "do"
  };
}
